import { useId, useState } from 'react';
import type { JSX, ReactNode } from 'react';
import { Button, type ButtonProps } from './button.js';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog.js';
import { Input } from './input.js';
import { Label } from './label.js';

/**
 * Dialogue de confirmation — `02` §0 : « actions sensibles (renvoyer,
 * régénérer un fichier déjà envoyé, forcer l'envoi, désactiver la maintenance
 * avec envoi, supprimer) : dialogue de confirmation qui rappelle la
 * conséquence ». `description` doit donc toujours nommer la conséquence
 * concrète, pas un texte générique (« Cette action est irréversible » seul ne
 * suffit pas).
 *
 * Pendant `isConfirming`, le dialogue ne se ferme pas (Échap, voile, bouton
 * Fermer ignorés) : l'issue de l'action s'affiche dans le dialogue (`error`)
 * plutôt que dans un toast éphémère.
 */
export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  confirmVariant?: ButtonProps['variant'];
  isConfirming?: boolean;
  /** Échec de l'action, affiché dans le dialogue resté ouvert. */
  error?: string | null | undefined;
  /**
   * Action catastrophique : texte à ressaisir à l'identique (espaces de bord
   * ignorés) pour activer la confirmation, en général le nom de l'objet.
   */
  verificationText?: string | undefined;
  onConfirm: () => void;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  confirmVariant = 'destructive',
  isConfirming = false,
  error = null,
  verificationText,
  onConfirm,
}: ConfirmDialogProps): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && isConfirming) {
          return;
        }
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <ConfirmDialogBody
          title={title}
          description={description}
          confirmLabel={confirmLabel}
          cancelLabel={cancelLabel}
          confirmVariant={confirmVariant}
          isConfirming={isConfirming}
          error={error}
          verificationText={verificationText}
          onCancel={() => {
            onOpenChange(false);
          }}
          onConfirm={onConfirm}
        />
      </DialogContent>
    </Dialog>
  );
}

interface ConfirmDialogBodyProps {
  title: string;
  description: ReactNode;
  confirmLabel: string;
  cancelLabel: string;
  confirmVariant: ButtonProps['variant'];
  isConfirming: boolean;
  error: string | null;
  verificationText: string | undefined;
  onCancel: () => void;
  onConfirm: () => void;
}

/** Monté à chaque ouverture (contenu Radix démonté à la fermeture) : la saisie repart de zéro. */
function ConfirmDialogBody({
  title,
  description,
  confirmLabel,
  cancelLabel,
  confirmVariant,
  isConfirming,
  error,
  verificationText,
  onCancel,
  onConfirm,
}: ConfirmDialogBodyProps): JSX.Element {
  const [typed, setTyped] = useState('');
  const inputId = useId();
  const errorId = useId();
  const verified = verificationText === undefined || typed.trim() === verificationText.trim();

  return (
    <form
      className="grid gap-5"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        // Le dialogue est rendu en portail : sans cela, l'envoi remonterait au
        // `<form>` React qui l'englobe éventuellement.
        event.stopPropagation();
        if (verified && !isConfirming) {
          onConfirm();
        }
      }}
    >
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription asChild={typeof description !== 'string'}>
          {typeof description === 'string' ? description : <div>{description}</div>}
        </DialogDescription>
      </DialogHeader>
      {verificationText === undefined ? null : (
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={inputId}>{`Saisissez « ${verificationText} » pour confirmer`}</Label>
          <Input
            id={inputId}
            autoComplete="off"
            spellCheck={false}
            value={typed}
            disabled={isConfirming}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
          />
        </div>
      )}
      {error === null ? null : (
        <p
          id={errorId}
          role="alert"
          className="rounded-md border border-status-red-border bg-status-red-bg px-3 py-2 text-sm text-status-red-fg"
        >
          {error}
        </p>
      )}
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={isConfirming}>
          {cancelLabel}
        </Button>
        <Button
          type="submit"
          variant={confirmVariant}
          isLoading={isConfirming}
          disabled={!verified || isConfirming}
          aria-describedby={error === null ? undefined : errorId}
        >
          {confirmLabel}
        </Button>
      </DialogFooter>
    </form>
  );
}

/**
 * Petite fabrique d'état pour les écrans qui déclenchent la confirmation
 * depuis une ligne de tableau (id de la cible à mémoriser pendant l'ouverture).
 * Optionnelle : un écran peut tout aussi bien gérer son propre `useState`.
 */
export function useConfirmDialog<TTarget>(): {
  target: TTarget | null;
  isOpen: boolean;
  open: (target: TTarget) => void;
  close: () => void;
} {
  const [target, setTarget] = useState<TTarget | null>(null);
  return {
    target,
    isOpen: target !== null,
    open: setTarget,
    close: () => {
      setTarget(null);
    },
  };
}
